import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Checkbox, Group, Loader, NumberInput, Paper, Select, SimpleGrid, Stack, Table, Text,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { IconBuildingBank } from '@tabler/icons-react';
import { useAccounts } from './accountsApi';
import { useBankReconciliation, useSetClearance } from './bankRecApi';

const fmt = (n: number) => new Intl.NumberFormat('ar-PS', { minimumFractionDigits: 2, numberingSystem: 'latn' }).format(n);
const toIso = (d: Date | null) => (d ?? new Date()).toISOString().slice(0, 10);

function StatCard({ label, value, color }: { label: string; value: number; color?: string }) {
  return (
    <Paper p="md" withBorder radius="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{label}</Text>
      <Text size="22px" fw={700} className="font-numeric" c={color} mt={4}>{fmt(value)}</Text>
    </Paper>
  );
}

export function BankReconciliationPage() {
  const { t, i18n } = useTranslation();
  const accounts = useAccounts(false);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [asOf, setAsOf] = useState<Date | null>(new Date());
  const [statementBalance, setStatementBalance] = useState<number>(0);
  const { data, isLoading } = useBankReconciliation(accountId, toIso(asOf));
  const setClearance = useSetClearance();

  const nameOf = (ar: string, en: string | null) => (i18n.language === 'ar' ? ar : (en ?? ar));
  // Cash/bank are assets — offer postable asset accounts.
  const bankOptions = (accounts.data ?? [])
    .filter((a) => a.isPostable && a.accountType === 1)
    .map((a) => ({ value: a.accountId, label: `${a.code} — ${nameOf(a.nameAr, a.nameEn)}` }));

  const toggle = (lineId: string, cleared: boolean) => {
    if (!accountId) return;
    setClearance.mutate({ accountId, lineIds: [lineId], clearanceDate: cleared ? toIso(asOf) : null });
  };

  const cleared = data?.clearedBalance ?? 0;
  const difference = statementBalance - cleared;
  const rows = data?.lines ?? [];

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.accounting')}
        title={t('accounting.bankRec.title')}
        subtitle={t('accounting.bankRec.subtitle')}
      />

      <Paper p="md">
        <Group gap="md" align="flex-end" wrap="wrap">
          <Select label={t('accounting.bankRec.account')} data={bankOptions} value={accountId}
            onChange={setAccountId} searchable w={300} placeholder={t('accounting.bankRec.selectAccount')} />
          <DatePickerInput label={t('accounting.bankRec.asOf')} value={asOf} onChange={setAsOf} valueFormat="YYYY-MM-DD" />
          <NumberInput label={t('accounting.bankRec.statementBalance')} value={statementBalance}
            onChange={(v) => setStatementBalance(typeof v === 'number' ? v : 0)} decimalScale={2} w={200} />
        </Group>
      </Paper>

      {!accountId ? (
        <Paper p="lg">
          <EmptyState icon={<IconBuildingBank size={22} stroke={1.6} />} title={t('accounting.bankRec.selectAccount')} description={t('accounting.bankRec.selectAccountBody')} />
        </Paper>
      ) : isLoading ? (
        <Stack align="center" py={48}><Loader size="sm" /></Stack>
      ) : (
        <>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
            <StatCard label={t('accounting.bankRec.ledgerBalance')} value={data?.ledgerBalance ?? 0} />
            <StatCard label={t('accounting.bankRec.clearedBalance')} value={cleared} />
            <StatCard label={t('accounting.bankRec.statementBalance')} value={statementBalance} />
            <StatCard label={t('accounting.bankRec.difference')} value={difference}
              color={Math.abs(difference) < 0.005 ? 'green' : 'red'} />
          </SimpleGrid>

          <Paper p={rows.length ? 0 : 'lg'}>
            {rows.length === 0 ? (
              <EmptyState icon={<IconBuildingBank size={22} stroke={1.6} />} title={t('accounting.bankRec.noLines')} description={t('accounting.bankRec.noLinesBody')} />
            ) : (
              <Table verticalSpacing="sm" highlightOnHover striped="even">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 48 }}>{t('accounting.bankRec.cleared')}</Table.Th>
                    <Table.Th>{t('accounting.bankRec.date')}</Table.Th>
                    <Table.Th>{t('accounting.bankRec.entry')}</Table.Th>
                    <Table.Th style={{ textAlign: 'end' }}>{t('accounting.bankRec.debit')}</Table.Th>
                    <Table.Th style={{ textAlign: 'end' }}>{t('accounting.bankRec.credit')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {rows.map((l) => (
                    <Table.Tr key={l.journalEntryLineId}>
                      <Table.Td>
                        <Checkbox
                          checked={l.clearanceDate !== null}
                          onChange={(e) => toggle(l.journalEntryLineId, e.currentTarget.checked)}
                          aria-label={t('accounting.bankRec.cleared')}
                        />
                      </Table.Td>
                      <Table.Td className="font-numeric">{l.entryDate}</Table.Td>
                      <Table.Td>
                        <Text size="sm"><span className="font-numeric font-mono text-xs text-[var(--wheels-ink-muted)]">{l.entryNumber}</span> {l.description ?? ''}</Text>
                      </Table.Td>
                      <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>{l.debit ? fmt(l.debit) : ''}</Table.Td>
                      <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>{l.credit ? fmt(l.credit) : ''}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        </>
      )}
    </Stack>
  );
}
